import React, { useState, useEffect, useRef } from 'react';

/** Normalize id (string or BSON { $oid }) to string so it is never rendered as object (React error #31). */
function toIdString(id) {
  if (id == null) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id !== null && typeof id.$oid === 'string') return id.$oid;
  return String(id);
}

/** Safe display string (never render an object). */
function toDisplayString(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null && typeof val.$oid === 'string') return val.$oid;
  return String(val);
}

const TenantFilter = ({ tenants, selectedTenants, onTenantFilterChange, style = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Ensure selectedTenants is always an array (handle undefined, null, or non-array values)
  const safeSelectedTenants = Array.isArray(selectedTenants) ? selectedTenants : [];
  
  // Only hide filter if there's 1 or fewer tenants (no need to filter)
  if (!tenants || tenants.length <= 1) return null;

  const handleOptionClick = (tenantId) => {
    if (tenantId === 'all') {
      // If "All Tenants" is clicked, deselect all tenants to show "All Tenants"
      // This ensures the state is always updated, even if already showing "All Tenants"
      if (safeSelectedTenants.length > 0) {
        // Deselect all to show "All Tenants"
        tenants.forEach(tenant => {
          onTenantFilterChange(toIdString(tenant.id), false);
        });
      }
      // Close dropdown after a brief delay to allow state update
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    } else {
      // Toggle single tenant
      const isSelected = safeSelectedTenants.includes(tenantId);
      onTenantFilterChange(tenantId, !isSelected);
      // Don't close dropdown for individual tenant selection (allows multi-select)
    }
  };

  const getDisplayText = () => {
    // Always ensure we have a valid array
    const selectedCount = safeSelectedTenants ? safeSelectedTenants.length : 0;
    
    if (selectedCount === 0) {
      return 'All Tenants';
    }
    
    if (selectedCount === 1 && tenants && Array.isArray(tenants)) {
      const sel = safeSelectedTenants[0];
      const tenant = tenants.find(t => t && toIdString(t.id) === toIdString(sel));
      if (tenant) {
        const name = toDisplayString(tenant.name);
        const id = toIdString(tenant.id);
        return name ? `${name} (${id})` : (id || 'Selected');
      }
    }
    
    return selectedCount > 1 ? `${selectedCount} tenants selected` : 'All Tenants';
  };

  return (
    <>
      <style>{`
        .tenant-filter-container {
          position: relative;
          display: inline-block;
        }

        .tenant-filter-button {
          padding: 8px 12px;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 0.875rem;
          background: var(--bg-secondary);
          color: var(--text-primary);
          cursor: pointer;
          width: 280px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }
        
        .tenant-filter-button.all-tenants {
          justify-content: flex-start;
        }
        
        .tenant-filter-button span:first-child {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
          margin-right: 8px;
        }
        
        .tenant-filter-button.all-tenants span:first-child {
          flex: 0;
          margin-right: auto;
        }
        
        @media (max-width: 768px) {
          .tenant-filter-button {
            width: 100% !important;
            min-width: 100% !important;
            min-height: 44px !important;
            height: auto !important;
            padding: 12px 16px !important;
            font-size: 0.938rem !important;
            border-radius: 8px !important;
          }
          
          .tenant-filter-dropdown {
            width: 100% !important;
            left: 0 !important;
            right: 0 !important;
            max-height: 300px !important;
            border-radius: 8px !important;
          }
          
          .tenant-filter-option {
            padding: 12px 16px !important;
            min-height: 44px !important;
            font-size: 0.938rem !important;
          }
        }

        .tenant-filter-button:hover {
          background: var(--bg-button);
          border-color: var(--border-color-strong);
        }

        .tenant-filter-button:focus {
          outline: 2px solid var(--primary);
          outline-offset: 2px;
        }

        .tenant-filter-arrow {
          font-size: 12px;
          color: var(--text-secondary);
          transform: rotate(0deg);
          transition: transform 0.2s ease;
        }

        .tenant-filter-arrow.open {
          transform: rotate(180deg);
        }

        .tenant-filter-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          width: 280px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          max-height: 200px;
          overflow-y: auto;
          overflow-x: visible;
          display: block !important;
          visibility: visible !important;
        }

        .tenant-filter-option {
          padding: 8px 12px;
          cursor: pointer;
          background: transparent;
          color: var(--text-primary, #2d3748) !important;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
          transition: background-color 0.2s ease;
          overflow: visible;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: relative;
          width: 100%;
          box-sizing: border-box;
          min-height: 32px;
          line-height: 1.5;
        }
        
        .tenant-filter-option:first-child {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: relative !important;
          top: 0 !important;
          left: 0 !important;
          margin: 0 !important;
          padding: 8px 12px !important;
          color: var(--text-primary, #2d3748) !important;
          background: transparent !important;
        }
        
        .tenant-filter-option:first-child::before {
          content: '';
          display: none;
        }

        .tenant-filter-option:last-child {
          border-bottom: none;
        }

        .tenant-filter-option:hover {
          background: var(--bg-button);
        }

        .tenant-filter-option.selected {
          background: var(--primary-light);
          color: var(--primary);
        }

        /* Dark mode specific adjustments */
        [data-theme="dark"] .tenant-filter-button {
          background: var(--bg-secondary);
          border-color: var(--border-color);
          color: var(--text-primary);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        [data-theme="dark"] .tenant-filter-button:hover {
          background: var(--bg-button);
          border-color: var(--border-color-strong);
        }

        [data-theme="dark"] .tenant-filter-dropdown {
          background: var(--bg-secondary);
          border-color: var(--border-color);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        }

        [data-theme="dark"] .tenant-filter-option {
          color: var(--text-primary);
          border-bottom-color: var(--border-color);
        }

        [data-theme="dark"] .tenant-filter-option:hover {
          background: var(--bg-button);
        }

        [data-theme="dark"] .tenant-filter-option.selected {
          background: var(--primary-light);
          color: var(--primary);
        }
      `}</style>
      <div ref={dropdownRef} className="tenant-filter-container" style={style}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`tenant-filter-button ${safeSelectedTenants.length === 0 ? 'all-tenants' : ''}`}
        >
          <span>{getDisplayText()}</span>
          <span className={`tenant-filter-arrow ${isOpen ? 'open' : ''}`}>
            ▼
          </span>
        </button>
        
        {isOpen && (
          <div className="tenant-filter-dropdown">
            {/* Always show "All Tenants" option first - MUST ALWAYS BE VISIBLE */}
            <div
              key="all-tenants-option"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOptionClick('all');
              }}
              className={`tenant-filter-option ${safeSelectedTenants.length === 0 ? 'selected' : ''}`}
              role="option"
              aria-selected={safeSelectedTenants.length === 0}
              data-testid="all-tenants-option"
              style={{
                color: safeSelectedTenants.length === 0 
                  ? 'var(--primary, #5897de)' 
                  : 'var(--text-primary, #2d3748)',
                display: 'block',
                visibility: 'visible',
                opacity: 1
              }}
            >
              All Tenants
            </div>
            {tenants && Array.isArray(tenants) && tenants.length > 0 && tenants.map(tenant => {
              const tid = toIdString(tenant.id);
              return (
                <div
                  key={tid || `tenant-${Math.random()}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionClick(tid);
                  }}
                  className={`tenant-filter-option ${safeSelectedTenants.some(s => toIdString(s) === tid) ? 'selected' : ''}`}
                >
                  {toDisplayString(tenant.name) || tid}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default TenantFilter;
